/* PlushBeds / Customatic Synergy. Standalone, scoped, dependency-free commerce.
 * No catalog fixtures. Native form works without JS; default AJAX handles errors
 * and redirects to the existing cart page, independent of a theme cart drawer.
 */
(() => {
  'use strict';
  const key = '__pbSynergyPdpV1';
  if (window[key]) { window[key].mount(document); return; }
  const instances = new WeakMap();
  class SynergyPdp {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.form = root.querySelector('.pb-synergy-product-form');
      this.input = root.querySelector('[data-variant-input]');
      this.quantity = root.querySelector('[data-quantity-input]');
      if (!this.form || !this.input || !this.quantity) return;
      try { this.variants = JSON.parse(root.querySelector('[data-variant-data]').textContent); }
      catch (_) { return; } // Leave working server-rendered form intact.
      this.busy = false;
      this.on(root, 'click', event => this.click(event));
      this.on(this.quantity, 'change', () => this.normalizeQuantity());
      this.on(this.form, 'submit', event => this.submit(event));
      this.on(root, 'keydown', event => this.galleryKeyboard(event));
      this.on(window, 'resize', () => this.setWidth());
      this.on(window, 'pageshow', () => { this.busy = false; this.setVariant(this.input.value, false); this.setWidth(); });
      this.on(window, 'popstate', () => {
        const id = new URL(location.href).searchParams.get('variant');
        if (id) this.setVariant(id, false);
      });
      this.setVariant(this.input.value, false);
      this.setWidth();
      root.classList.add('synergy-ready');
    }
    on(target, type, listener) { target.addEventListener(type, listener, { signal: this.abort.signal }); }
    all(selector) { return this.root.querySelectorAll(selector); }
    setWidth() {
      if (this.root.dataset.fullBleed === 'true') this.root.style.setProperty('--pb-synergy-viewport', `${document.documentElement.clientWidth}px`);
    }
    setVariant(id, updateUrl = true) {
      const variant = this.variants.find(v => v.id === String(id));
      if (!variant || this.busy) return;
      this.variant = variant;
      this.input.value = variant.id;
      this.all('[data-variant]').forEach(link => {
        const selected = link.dataset.variant === variant.id;
        link.classList.toggle('selected', selected);
        link.setAttribute('aria-current', String(selected));
      });
      this.all('[data-price]').forEach(el => { el.textContent = variant.price; });
      this.all('[data-compare]').forEach(el => { el.textContent = variant.compare; el.hidden = !variant.onSale; });
      this.all('[data-saving]').forEach(el => { el.textContent = `Save ${variant.saving}`; el.hidden = !variant.onSale; });
      this.all('[data-size-name]').forEach(el => { el.textContent = `— ${variant.title}`; });
      this.quantity.min = variant.min;
      this.quantity.step = variant.step;
      if (variant.max !== null) this.quantity.max = variant.max;
      else this.quantity.removeAttribute('max');
      this.normalizeQuantity();
      const rules = this.root.querySelector('[data-quantity-rules]');
      rules.hidden = variant.min === 1 && variant.step === 1 && variant.max === null;
      rules.textContent = `Minimum ${variant.min} · Increments of ${variant.step}${variant.max === null ? '' : ` · Maximum ${variant.max}`}`;
      this.updateButtons();
      this.root.querySelector('[data-cart-error]').hidden = true;
      if (updateUrl) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState(history.state, '', url);
      }
      this.root.dispatchEvent(new CustomEvent('pb:synergy:variant-change', {
        bubbles: true, detail: { productId: this.root.dataset.productId, variantId: variant.id, variant: { ...variant } }
      }));
    }
    normalizeQuantity() {
      const min = Math.max(1, Number(this.quantity.min) || 1);
      const step = Math.max(1, Number(this.quantity.step) || 1);
      const max = this.quantity.hasAttribute('max') ? Number(this.quantity.max) : Infinity;
      const raw = Number(this.quantity.value);
      let value = min + Math.max(0, Math.round(((Number.isFinite(raw) ? raw : min) - min) / step)) * step;
      if (value > max) value = min + Math.max(0, Math.floor((max - min) / step)) * step;
      this.quantity.value = String(value);
      this.all('[data-quantity]').forEach(button => {
        button.disabled = this.busy || (Number(button.dataset.quantity) < 0 ? value <= min : value + step > max);
      });
    }
    updateButtons() {
      this.all('[data-cart]').forEach(button => {
        button.disabled = this.busy || !this.variant.available;
        button.textContent = this.busy ? 'Adding…' : this.variant.available ? 'Add to Cart' : 'Sold Out';
        if (button.classList.contains('gold-button') && !this.busy && this.variant.available) {
          const arrow = document.createElement('span'); arrow.textContent = '→'; arrow.setAttribute('aria-hidden', 'true'); button.append(arrow);
        }
        button.setAttribute('aria-busy', String(this.busy));
      });
      this.all('[data-variant]').forEach(link => {
        if (this.busy) link.setAttribute('aria-disabled', 'true'); else link.removeAttribute('aria-disabled');
      });
      this.quantity.readOnly = this.busy;
      this.normalizeQuantity();
    }
    click(event) {
      const target = event.target.closest('a,button');
      if (!target || !this.root.contains(target)) return;
      if (target.hasAttribute('data-variant')) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); this.setVariant(target.dataset.variant); return;
      }
      if (target.hasAttribute('data-quantity') && !this.busy) {
        this.quantity.value = String(Number(this.quantity.value) + Number(target.dataset.quantity) * Number(this.quantity.step));
        this.normalizeQuantity(); return;
      }
      if (target.hasAttribute('data-gallery')) {
        const img = this.root.querySelector('.hero-image');
        img.src = target.dataset.gallery; img.alt = target.dataset.galleryAlt;
        this.all('[data-gallery]').forEach(button => {
          const active = target === button; button.classList.toggle('active', active); button.setAttribute('aria-pressed', String(active));
        });
        return;
      }
      if (target.hasAttribute('data-position')) {
        const head = target.dataset.position === 'head-up';
        const img = this.root.querySelector('.position-image');
        img.src = target.dataset.positionSrc;
        img.alt = head ? 'Exact Synergy base with head raised' : 'Exact Synergy base with head and foot raised';
        const caption = this.root.querySelector('[data-position-caption]');
        caption.textContent = head ? 'HEAD UP ' : 'HEAD + FOOT ';
        const sub = document.createElement('span');
        sub.textContent = head ? 'A little elevation. A much better reading spot.' : 'Put your feet up. Settle into your own kind of comfort.';
        caption.append(sub);
        this.all('[data-position]').forEach(button => { const active = target === button; button.classList.toggle('active', active); button.setAttribute('aria-pressed', String(active)); });
        return;
      }
      if (target.hasAttribute('data-zoom')) {
        const source = this.root.querySelector('.hero-image');
        const dialog = this.root.querySelector('[data-image-dialog]');
        dialog.querySelector('img').src = source.src; dialog.querySelector('img').alt = source.alt;
        dialog.showModal(); return;
      }
      if (target.hasAttribute('data-size-guide')) {
        const dialog = this.root.querySelector('[data-size-dialog]');
        if (dialog && typeof dialog.showModal === 'function') { event.preventDefault(); dialog.showModal(); }
        return;
      }
      if (target.hasAttribute('data-close-image') || target.hasAttribute('data-close-size')) target.closest('dialog').close();
    }
    galleryKeyboard(event) {
      const buttons = [...this.all('[data-gallery]')];
      const i = buttons.indexOf(event.target);
      if (i < 0 || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (i + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
      buttons[next].focus(); buttons[next].click();
    }
    async submit(event) {
      if (this.busy || !this.variant.available) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      this.normalizeQuantity();
      if (!this.form.checkValidity()) { event.preventDefault(); this.form.reportValidity(); return; }
      if (this.root.dataset.cartMode === 'native') return;
      event.preventDefault();
      // Avoid duplicate submissions from generic delegated theme cart handlers.
      event.stopImmediatePropagation();
      const error = this.root.querySelector('[data-cart-error]');
      const status = this.root.querySelector('[data-cart-status]');
      error.hidden = true; status.textContent = 'Adding to cart';
      const body = new FormData(this.form); // Includes any installed line-item property inputs.
      this.busy = true; this.updateButtons();
      this.request = new AbortController();
      const timeout = setTimeout(() => this.request.abort(), 20000);
      try {
        const response = await fetch(this.root.dataset.cartAddUrl, {
          method:'POST', headers:{ Accept:'application/json' }, body, credentials:'same-origin', signal:this.request.signal
        });
        const data = await response.json();
        if (!response.ok || data.status >= 400) {
          const message = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : 'This item could not be added. Please check the selected size and quantity.';
          throw Object.assign(new Error(message), { shopifyResponse: true });
        }
        status.textContent = 'Added to cart. Opening your cart.';
        this.root.dispatchEvent(new CustomEvent('pb:synergy:cart-added', { bubbles:true, detail:{ productId:this.root.dataset.productId, variantId:this.variant.id, response:data } }));
        window.location.assign(this.root.dataset.cartUrl);
      } catch (err) {
        if (!this.root.isConnected) return;
        error.textContent = err.shopifyResponse ? err.message : 'We could not confirm the cart update. Check your cart before trying again.';
        if (!err.shopifyResponse) {
          const link = document.createElement('a'); link.href = this.root.dataset.cartUrl; link.textContent = ' View cart'; link.style.textDecoration = 'underline'; error.append(link);
        }
        error.hidden = false; status.textContent = '';
        this.busy = false; this.updateButtons(); error.focus();
      } finally { clearTimeout(timeout); }
    }
    destroy() { this.abort.abort(); if (this.request) this.request.abort(); }
  }
  const mount = scope => scope.querySelectorAll('[data-pb-synergy]').forEach(root => {
    if (!instances.has(root)) instances.set(root, new SynergyPdp(root));
  });
  window[key] = { mount };
  mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-pb-synergy]').forEach(root => {
    instances.get(root)?.destroy(); instances.delete(root);
  }));
})();
